import {
  DateTimeValueSchemas,
  getSetIsSupersetOf,
  type LogicalOperatorsType,
} from "@validereinc/utilities";
import { cloneDeep, isPlainObject } from "es-toolkit/compat";
import { HeaderType, type ItemBaseType } from "../DataTable/types";
import { SelectPopoverOptionType } from "../SelectPopover/types";

/**
 * Representation of a table header when configured
 * @see DataTable component
 */
export type ConfiguredDataTableHeader = {
  key: string;
  isFixed?: boolean;
  isSelected?: boolean;
  headers?: ConfiguredDataTableHeader[];
};

/**
 * Representation of a table when configured
 * @see DataTable component
 */
export type ConfiguredDataTable = {
  source: "user-default" | "user" | "code";
  headers: ConfiguredDataTableHeader[];
};

/**
 * Convert an array of headers to a Map of the header key to the header
 * itself
 */
export const makeMapFromHeaders = <ItemType extends ItemBaseType>(
  headers: Array<HeaderType<ItemType>>,
  map: Map<string, HeaderType<ItemType>> = new Map()
) =>
  headers.reduce<Map<string, HeaderType<ItemType>>>((map, header) => {
    if (header.headers) {
      map = makeMapFromHeaders(header.headers, map);
    }

    map.set(header.key, header);
    return map;
  }, map);

/**
 * Convert an array of headers to a Map of all the group by keys to the header
 * itself
 */
export const makeGroupByMapFromHeaders = <ItemType extends ItemBaseType>(
  headers: Array<HeaderType<ItemType>>,
  map: Map<string, HeaderType<ItemType>> = new Map()
) =>
  headers.reduce<Map<string, HeaderType<ItemType>>>((map, header) => {
    if (header.headers) {
      map = makeGroupByMapFromHeaders(header.headers, map);
    }

    header.groupByKeys?.forEach((groupByKey) => {
      map.set(groupByKey, header);
    });
    return map;
  }, map);

/**
 * Convert an array of header configurations to a Map of the header config
 * key to the header config itself
 */
export const makeMapFromHeaderConfigs = (
  headers: ConfiguredDataTableHeader[],
  map: Map<string, ConfiguredDataTableHeader> = new Map()
) =>
  headers.reduce<Map<string, ConfiguredDataTableHeader>>((map, header) => {
    if (header.headers) {
      map = makeMapFromHeaderConfigs(header.headers, map);
    }

    map.set(header.key, header);
    return map;
  }, map);

/**
 * Given a header configuration and an existing header configuration, compare them to see if they are different.
 * @param newHeaderConfig given header configuration that is the source of truth
 * @param existingHeaderConfig the persisted header configuration to check
 * @returns true if the given header configuration has been configured, false otherwise
 */
export const checkIfHeadersConfigured = (
  newHeaderConfig: ConfiguredDataTableHeader[],
  existingHeaderConfig?: ConfiguredDataTableHeader[]
): boolean => {
  if (!existingHeaderConfig?.length) return false;

  return (
    newHeaderConfig.length !== existingHeaderConfig.length ||
    newHeaderConfig.some((config, idx) => {
      const existingConfig = existingHeaderConfig[idx];

      if (config.headers?.length !== existingConfig.headers?.length)
        return true;
      if (config.headers || existingConfig.headers) {
        // if nested headers are configured, exit early, otherwise, continue with other checks
        if (
          checkIfHeadersConfigured(
            config.headers ?? [],
            existingConfig.headers ?? []
          )
        )
          return true;
      }

      return (
        config.key !== existingConfig.key ||
        config.isSelected !== existingConfig.isSelected ||
        config.isFixed !== existingConfig.isFixed
      );
    })
  );
};

/**
 * Is a given list of header configurations valid / up-to-date with the list of
 * headers defined in code? If not, the header configurations have to be reset or corrected.
 * @param persistedHeadersMap the configuration of headers persisted in the user's storage system
 * @param codeHeadersMap the configuration of headers defined in the code/database
 * @returns true if the header configurations are all valid, false otherwise
 */
export const checkIfHeaderConfigsValid = <ItemType extends ItemBaseType>(
  persistedHeadersMap: Map<string, ConfiguredDataTableHeader>,
  codeHeadersMap: Map<string, HeaderType<ItemType>>
): boolean => {
  const headersToCheckAgainst = new Set(codeHeadersMap.keys());
  const headersToCheck = new Set(persistedHeadersMap.keys());

  if (!headersToCheck.size) return true;

  // headers from code/database should always be a superset of the headers defined in the user's environment
  return getSetIsSupersetOf(headersToCheckAgainst, headersToCheck);
};

/**
 * Merge two header configurations together, using the provided map to merge on
 * top of the subject, but using the subject as the base (i.e. entries will not
 * be added or removed, only modified)
 * @param subjectHeaderConfigs the header configurations to merge on top of
 * @param headerConfigsToMergeMap a map of header keys to configurations that
 * will be used to check against and then merged onto the subject with
 * @returns a consolidated header configurations array
 */
export const mergeHeaderConfigs = (
  subjectHeaderConfigs: ConfiguredDataTableHeader[],
  headerConfigsToMergeMap: Map<string, ConfiguredDataTableHeader>
): ConfiguredDataTableHeader[] => {
  return cloneDeep(subjectHeaderConfigs).map((config) => {
    const copyOfConfig = { ...config };

    if (copyOfConfig.headers) {
      copyOfConfig.headers = mergeHeaderConfigs(
        copyOfConfig.headers,
        headerConfigsToMergeMap
      );
    }

    if (headerConfigsToMergeMap.get(config.key)) {
      return {
        ...copyOfConfig,
        ...headerConfigsToMergeMap.get(config.key),
      };
    }

    return copyOfConfig;
  });
};

/**
 * Get a header configuration from a header
 * @param header a header
 * @returns a header configuration
 */
export const getHeaderConfigFromHeader = <ItemType extends ItemBaseType>(
  header: HeaderType<ItemType>
): ConfiguredDataTableHeader => {
  const nestedHeaders = header.headers
    ? header.headers.map((h) => getHeaderConfigFromHeader(h))
    : [];

  return {
    key: header.key,
    isFixed: header.isFixed ?? false,
    isSelected: nestedHeaders.length
      ? nestedHeaders.every((h) => h.isSelected)
      : (header.isSelected ?? true),
    ...(nestedHeaders.length ? { headers: nestedHeaders } : {}),
  };
};

/**
 * Get a list of header configurations from a list of headers
 * @param headers list of headers
 * @returns list of header configurations
 */
export const getHeaderConfigFromHeaders = <ItemType extends ItemBaseType>(
  headers: Array<HeaderType<ItemType>>
): ConfiguredDataTableHeader[] => {
  return headers.map((h) => getHeaderConfigFromHeader(h));
};

/**
 * Translate a header configuration to an option compatible with the SelectPopover component
 * @param headerConfig header configuration to translate
 * @param headersMap a Map of header keys to the full header details that will be used to enrich the config option
 * @param optOverrides configuration option fields to override
 * @see {@link SelectPopover}
 * @returns a configuration option
 */
export const getOptionFromHeaderConfig = <ItemType extends ItemBaseType>(
  headerConfig: ConfiguredDataTableHeader,
  headersMap: Map<string, HeaderType<ItemType>>,
  optOverrides: Partial<SelectPopoverOptionType> = {}
): SelectPopoverOptionType => {
  const associatedHeader = headersMap.get(headerConfig.key);

  if (!associatedHeader) {
    return {
      label: `${headerConfig.key} (deprecated)`,
      value: headerConfig.key,
      isPinned: false,
      options: undefined,
    };
  }

  return {
    label: associatedHeader.label ?? headerConfig.key,
    value: associatedHeader.key,
    isPinned: Boolean(
      headerConfig?.isFixed ?? associatedHeader.isFixed ?? false
    ),
    ...optOverrides,
    options: headerConfig.headers
      ? headerConfig.headers.map((nestedHeaderConfig) => {
          return getOptionFromHeaderConfig(nestedHeaderConfig, headersMap, {
            isPinned: Boolean(
              headerConfig?.isFixed ?? associatedHeader.isFixed ?? false
            ),
          });
        })
      : undefined,
  };
};

/**
 * Translate a list of header configurations to a list of options compatible with the SelectPopover component
 * @param headerConfig list of header configurations to translate
 * @param headersMap a Map of header keys to the full header details that will be used to enrich the config options
 * @see {@link SelectPopover}
 * @returns a list of configuration options
 */
export const getOptionsFromHeaderConfigs = <ItemType extends ItemBaseType>(
  headerConfigs: ConfiguredDataTableHeader[],
  headersMap: Map<string, HeaderType<ItemType>>
): SelectPopoverOptionType[] =>
  headerConfigs.map<SelectPopoverOptionType>((config) =>
    getOptionFromHeaderConfig(config, headersMap)
  );

/**
 * Get a list of selected header keys from a header configuration
 * @param configuredHeaders header configuration
 * @returns list of selected headers represented by their keys
 */
export const getSelectedHeaderKeysFromHeaderConfig = (
  configuredHeaders: ConfiguredDataTableHeader[]
): string[] => {
  return configuredHeaders.reduce<string[]>((value, config) => {
    if (config.headers) {
      value.push(...getSelectedHeaderKeysFromHeaderConfig(config.headers));
      return value;
    }

    if (config.isSelected) value.push(config.key);
    return value;
  }, []);
};

/**
 * Get a list of selected header keys from a list of headers
 * @param headers list of headers. any not explicitly de-selected are assumed to be selected.
 * @returns list of selected headers represented by their keys
 */
export const getSelectedHeaderKeysFromHeaders = <ItemType extends ItemBaseType>(
  headers: Array<HeaderType<ItemType>>
): string[] => {
  return headers.reduce<string[]>((value, header) => {
    if (header.headers) {
      value.push(...getSelectedHeaderKeysFromHeaders(header.headers));
      return value;
    }

    if (header.isSelected ?? true) value.push(header.key);
    return value;
  }, []);
};

/**
 * Get headers from a header configuration list
 * @param headerConfig header configuration list
 * @param headersMap a Map of header keys to the full header details that will be used to enrich the configuration list
 * @returns a list of headers that have the header configuration applied
 */
export const getHeadersFromHeaderConfigs = <ItemType extends ItemBaseType>(
  headerConfigs: ConfiguredDataTableHeader[],
  headersMap: Map<string, HeaderType<ItemType>>
): Array<HeaderType<ItemType>> =>
  headerConfigs.reduce<Array<HeaderType<ItemType>>>((headers, config) => {
    const isGroup = !!config.headers;

    if ((!config.isSelected && !isGroup) || !headersMap.has(config.key))
      return headers;

    if (isGroup) {
      const configuredSubHeaders = getHeadersFromHeaderConfigs(
        config.headers ?? [],
        headersMap
      );

      if (!configuredSubHeaders.length) return headers;

      headers.push({
        ...headersMap.get(config.key)!,
        isFixed: config.isFixed,
        headers: configuredSubHeaders,
      });
    } else {
      headers.push({
        ...headersMap.get(config.key)!,
        isFixed: config.isFixed,
      });
    }

    return headers;
  }, []);

/**
 * Get the header configuration for a single configuration
 * @param opt the config option
 * @param selectedHeaderKeys the selected config options
 * @returns a header configuration that can be persisted
 */
export const getHeaderConfigFromConfigOption = (
  opt: SelectPopoverOptionType,
  selectedHeaderKeys: string[]
): ConfiguredDataTableHeader => {
  const nestedHeaderConfigs = opt.options
    ? opt.options.map((o) =>
        getHeaderConfigFromConfigOption(o, selectedHeaderKeys)
      )
    : [];

  return {
    key: opt.value,
    isFixed: opt?.isPinned ?? false,
    isSelected: nestedHeaderConfigs.length
      ? nestedHeaderConfigs.every((h) => h.isSelected)
      : selectedHeaderKeys.includes(opt.value),
    ...(nestedHeaderConfigs.length
      ? {
          headers: nestedHeaderConfigs,
        }
      : {}),
  };
};

/**
 * Translate a list of options from the SelectPopover component to a list of header configurations
 * @param options the config options
 * @param selectedHeaderKeys the selected config options
 * @returns a header configuration list
 */
export const getHeaderConfigsFromOptions = (
  options: SelectPopoverOptionType[],
  selectedHeaderKeys: string[]
): ConfiguredDataTableHeader[] =>
  options.map<ConfiguredDataTableHeader>((opt) =>
    getHeaderConfigFromConfigOption(opt, selectedHeaderKeys)
  );

/**
 * Check if a selection state contains a given list of items. The default
 * operator is $or which means if any of the items are in the selection, the
 * function will return true.
 */
export const areItemsInSelection = <ItemType extends ItemBaseType>(
  selection: Record<string, ItemType>,
  items: ItemType[],
  operation: Extract<LogicalOperatorsType, "$and" | "$or"> = "$or"
) => {
  return operation === "$and"
    ? Object.keys(selection).every((key) =>
        items.some((item) => item.id === key)
      )
    : Object.keys(selection).some((key) =>
        items.some((item) => item.id === key)
      );
};

/**
 * Get the value and key for a group by entry
 * @param groupItem the group by entry
 * @returns the value and key for the group by entry
 */
export const getDataTableGroupByValueAndKeyForGroupByEntry = <
  T extends { value: unknown; count: number },
>(
  groupItem: T
): [T["value"], string] => {
  // group by values are stringified usually from APIs, so we need to parse them
  const value = safeParseGroupByValue(groupItem.value);

  if (Array.isArray(value)) {
    return [value, value.length >= 1 ? JSON.stringify(value) : "-"];
  }

  if (isPlainObject(value)) {
    return [
      value,
      Object.keys(value as object).length ? JSON.stringify(value) : "-",
    ];
  }

  if (DateTimeValueSchemas.dateTimeStrict.safeParse(value).success) {
    return [value, (value as Date).toISOString()];
  }

  return [value, value !== undefined && value !== null ? String(value) : "-"];
};

/**
 * Get the keys for a list of group by entries
 * @param groups the list of group by entries
 * @returns the keys for the group by entries
 */
export const getDataTableGroupByKeysForGroupByData = <
  T extends { value: unknown; count: number },
>(
  groups: T[]
): string[] => {
  return groups
    .map((group) => getDataTableGroupByValueAndKeyForGroupByEntry(group)[1])
    .filter((k) => k !== undefined);
};

/**
 * Parse a group by value
 * @internal
 * @param value the value to parse
 * @returns the parsed value
 */
function safeParseGroupByValue(value: any) {
  try {
    return JSON.parse(value);
  } catch (error) {
    return value;
  }
}
